import * as React from 'react';

import PropTypes from 'prop-types';
import _ from 'underscore';
import capitalize from 'underscore.string/capitalize';

import {deserializeUrlParam} from 'nutshell-core/deserialize-url-param';
import {leadPriorityIsHot} from 'nutshell-core/leads/lead-priority-is-hot';
import {serialize} from 'nutshell-core/serialize';

import {Button} from 'shells/button/button';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
import {RelativeOverlay} from 'shells/relative-overlay/relative-overlay';

import {appHistory} from '../../history';

import styles from './map-view.module.css';

export class MapView extends React.Component {
    static propTypes = {
        mapFilter: PropTypes.shape({
            map: PropTypes.shape({
                latitude: PropTypes.string.isRequired,
                longitude: PropTypes.string.isRequired,
                northEast: PropTypes.string.isRequired,
                southWest: PropTypes.string.isRequired,
                radius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            }),
        }),
        changeView: PropTypes.func.isRequired,
        entities: PropTypes.object.isRequired,
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
    };

    componentDidMount() {
        const map = this.renderMapLayer();
        const featureLayer = this.renderFeatureLayer(map);
        const {
            entities: {items, entityData},
            mapFilter,
        } = this.props;

        // The newMapId is the ID of the Mapbox Studio Styles created
        // on Mapbox.com
        //
        // We use these same styles for the peep page maps.
        const mapboxStudioStylesURL = `mapbox://styles/${MapboxConfig.newMapId}`;

        // Render style layer
        L.mapbox.styleLayer(mapboxStudioStylesURL, {minZoom: 2}).addTo(map);

        // Generate our geoJson
        const geoJson = this.generateGeoJson(items.map((id) => entityData[id]));
        featureLayer.setGeoJSON(geoJson);

        // Set initial map view - I think this is necessary but maybe not
        map.invalidateSize();

        // If we have existing bounds to match, use those.
        if (mapFilter) {
            const deserializedData = deserializeUrlParam(mapFilter.map, 'map');
            _.defer(() => map.fitBounds(deserializedData.bounds));
        } else {
            map.fitWorld();
        }

        // Set up map listeners to update the filter; have to defer so that the
        // DOM is in the correct state before we start listening for changes, or
        // it will mutilate our filter coords
        _.defer(() => {
            map.on('zoomend', this.updateMapFilter);
            map.on('dragend', this.updateMapFilter);
            map.on('resize', this.updateMapFilter);
        });

        $('#ui-entity-list-map').on('click', '.action-schedule-activity', function (e) {
            const data = $(e.target).data();
            const urlData = {};

            if (String(data.entityType) === 'Leads') {
                urlData.leadId = data.entityId.split('-')[0];
            } else {
                urlData.participants = [{entityName: data.entityType, entityId: data.entityId}];
            }

            appHistory.replace({
                ...appHistory.location,
                hash: `/create/activities`,
                state: {modal: true},
            });
        });

        this.setState({
            featureLayer,
            map,
            hasView: true,
        });
    }

    componentDidUpdate(prevProps) {
        const {width, height} = prevProps;
        const {map, hasView} = this.state;
        if ((this.props.width !== width || this.props.height !== height) && hasView) {
            map.invalidateSize();
        }
    }

    UNSAFE_componentWillReceiveProps(newProps) {
        const {
            entities: {items, entityData},
            mapFilter,
        } = newProps;
        const {featureLayer, hasView, map, fittedBounds} = this.state;

        if (this.props.entities.items !== items && hasView && items.length) {
            const geoJson = this.generateGeoJson(items.map((id) => entityData[id]));

            // It's possible that we couldn't geocode any addresses.  Maybe the
            // entities had no address.  If we proceed, mapbox throws an error.
            if (geoJson.length) {
                featureLayer.setGeoJSON(geoJson);
                if (!mapFilter && !fittedBounds) {
                    // If no filter, bound to map markers when they come back the first time
                    map.fitBounds(featureLayer.getBounds());
                    this.setState({fittedBounds: true});
                }
            }
        }
    }

    componentWillUnmount() {
        if (this.state.clearFilterOnUnmount) {
            this.props.entities.query.filter.removeFilters(this.props.mapFilter);
        }
    }

    constructor(props) {
        super(props);
        this.state = {
            hasView: false,
            clearFilterOnUnmount: true,
        };
    }

    updateMapFilter = () => {
        const {
            query: {
                filter: {addFilters, updateFilter},
            },
        } = this.props.entities;
        const {map} = this.state;
        const bounds = map.getBounds();
        const southWest = bounds._southWest;
        const northEast = bounds._northEast;
        const center = map.getCenter();

        const data = {
            map: {
                bounds: map.getBounds(),
                latitude: center.lat,
                longitude: center.lng,
                radius: Math.round(southWest.distanceTo(northEast) / 1609.34 / 2),
            },
        };

        const serializedData = serialize(data, 'map');

        // If we have an existing map filter, update it. otherwise, add a new one.
        if (this.props.mapFilter) {
            updateFilter(this.props.mapFilter, serializedData);
        } else {
            addFilters(serializedData);
        }
    };

    render() {
        const style = {
            height: this.props.height,
            width: this.props.width,
        };

        const {hasError, firstLoad} = this.props.entities;
        const {map, locating} = this.state;

        return (
            <div className='ui-entity-list__map-wrapper'>
                <div id='ui-entity-list-map' style={style} />
                <div className={styles.buttons}>
                    <Button variant='primary' size='big' onClick={this.viewList}>
                        View as list
                    </Button>
                    <Button variant='primary' size='big' onClick={(e) => this.locateMe(e, map)}>
                        Find me
                    </Button>
                    <Button variant='primary' size='big' onClick={() => map.fitWorld()}>
                        See the world
                    </Button>
                </div>
                {(firstLoad && !hasError) || locating ? (
                    <RelativeOverlay style={{zIndex: '20'}} backgroundColor='rgba(255,255,255,0.8)'>
                        <div className='ui-entity-list__loading-state'>
                            <LoadingIcon />
                        </div>
                    </RelativeOverlay>
                ) : undefined}
            </div>
        );
    }

    viewList = () => {
        this.setState(
            {
                clearFilterOnUnmount: false,
            },
            () => {
                setTimeout(() => {
                    this.props.changeView('list');
                }, 0);
            }
        );
    };

    locateMe(e, map) {
        e.preventDefault();
        e.stopPropagation();

        map.locate({
            setView: true,
            enableHighAccuracy: true,
        });

        this.setState({locating: true});
    }

    placeMyLocationMarker(map) {
        if (this.state.locationFound) return;

        const bounds = map.getCenter();
        const popupContent = "<h1>That's you!</h1>";

        L.marker(bounds)
            .setIcon(
                L.divIcon({
                    className: 'ui-marker ui-marker--type-user',
                    html: "<div class='ui-marker-icon'></div>",
                    iconSize: [50, 70],
                })
            )
            .bindPopup(popupContent, {
                closeButton: false,
                offset: [0, -30],
                className: 'user-popup',
            })
            .addTo(map);

        this.setState({locationFound: true});
    }

    renderMapLayer() {
        L.mapbox.accessToken = MapboxConfig.apiKey;

        const map = L.mapbox.map('ui-entity-list-map', null, {
            minZoom: 2,
            maxZoom: 17,
            attributionControl: false,
            maxBounds: L.latLngBounds(L.latLng(-90, -180), L.latLng(90, 180)),
        });

        map.addControl(L.mapbox.geocoderControl('mapbox.places'));
        map.on('locationfound', (e) => {
            map.fitBounds(e.bounds, {maxZoom: 13});
            this.setState({locating: false});
            this.placeMyLocationMarker(map);
        });

        map.on('locationerror', () => this.setState({locating: false}));

        return map;
    }

    renderFeatureLayer(map) {
        const featureLayer = L.mapbox.featureLayer().addTo(map);

        featureLayer.on('layeradd', (e) => {
            const marker = e.layer;

            // Initially setting this to null, we want one reference to this variable so
            // We can properly remove other instances of hover popups when new ones arrive.
            let hoverPopup = null;

            marker.setIcon(
                L.divIcon({
                    className: `ui-marker ui-marker--type-${marker.feature.properties.entityType} ${
                        marker.feature.properties.isHot ? 'hot' : ''
                    }`,
                    html: "<div class='ui-marker-icon'></div>",
                    iconSize: [30, 45],
                })
            );

            marker.on('click', () => {
                if (hoverPopup) map.removeLayer(hoverPopup);

                const popup = this.generateMapPopup(marker.feature);

                marker
                    .bindPopup(popup, {
                        minWidth: 320,
                        offset: [0, -20],
                        className: `type-${marker.feature.properties.entityType}`,
                    })
                    .openPopup();
            });

            marker.on('mouseover', () => {
                if (marker.getPopup()) return;
                if (hoverPopup) map.removeLayer(hoverPopup);

                // Create hover popup container
                hoverPopup = L.popup({
                    closeButton: false,
                    offset: [0, -20],
                    className: 'hover-popup',
                    minWidth: 120,
                }).setLatLng(marker.getLatLng());

                // Generate hover popup content
                const popupContent = this.generateHoverPopup(marker.feature);

                // Set hover popup content
                hoverPopup.setContent(popupContent);

                // Actually add it to the map layer
                map.addLayer(hoverPopup);
            });

            marker.on('popupclose', () => marker.unbindPopup());
            marker.on('mouseout', () => map.removeLayer(hoverPopup));
        });

        return featureLayer;
    }

    generateGeoJson(items) {
        const geoJson = items
            .filter((entity) => !(_.isNull(entity.latlon) || _.isUndefined(entity.latlon))) // Ensure we got latlon to display
            .map((entity) => {
                const marker = {
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: entity.fuzzed
                            ? [entity.fuzzed[1], entity.fuzzed[0]]
                            : [entity.latlon[1], entity.latlon[0]],
                    },
                    properties: {
                        id: entity.links.entity.id,
                        name: entity.primaryName,
                        mapUrl: entity.mapUrl,
                        initials: entity.initials,
                        entityName: capitalize(entity.links.entity.type),
                        entityType: entity.links.entity.type,
                        relatedName: entity.relatedName,
                        relatedType: entity.relatedType,
                        relatedUrl: entity.relatedUrl,
                        htmlUrl: entity.htmlUrl,
                        avatarUrl: entity.avatarUrl,
                        address: entity.address,
                        isHot:
                            entity.links.entity.type === 'leads'
                                ? leadPriorityIsHot(entity.priority)
                                : null,
                    },
                };

                return marker;
            });

        return geoJson;
    }

    /*
     * It's much easier to just return a big chunk of HTML for the Mapbox Leaflet API to use. This is
     * why we don't return a component or chunk of JSX here.
     */
    generateMapPopup(feature) {
        const {
            id,
            name,
            htmlUrl,
            entityName,
            entityType,
            mapUrl,
            relatedName,
            relatedType,
            relatedUrl,
            initials,
            avatarUrl,
        } = feature.properties;

        const relatedText = relatedName ? relatedName : '';

        return `<div class='clearfix custom-leaflet'>
                <div class='float-left'>
                    <div class='ui-avatar-container'>
                        <div class='ui-avatar type-${entityType}'>
                            <div class='initials'>${initials}</div>
                            <img src='${avatarUrl}'></img>
                        </div>
                    </div>
                    <p class='name'><a href=${htmlUrl}>${name}</a></p>
                    <p class='related-entity type-${relatedType}'><a href=${relatedUrl}>${relatedText}</a></p>
                    <div class='action-bar'>
                        <a href=${mapUrl} target='_blank'>Google Maps</a>
                        <a class='action-schedule-activity float-right' href='#' data-entity-type='${entityName}' data-entity-id='${id}'>Schedule activity</a>
                    </div>
                </div>
            </div>`;
    }

    /*
     * See above method - not returning component since Mapbox expects raw HTML.
     */
    generateHoverPopup(feature) {
        const {name, entityType, relatedType, relatedName} = feature.properties;

        const relatedText = relatedName ? relatedName : '';

        return `<div>
                <p class='hover-primary type-${entityType}'>${name}</p>
                <p class='hover-secondary type-${relatedType}'>${relatedText}</p>
            </div>`;
    }
}
